import React from 'react';
import { Download, CheckCircle, Zap, AlertCircle, ExternalLink } from 'lucide-react';

/**
 * EA Download page — separate cards for MT4 and MT5 with features + setup steps.
 */
export default function EaDownload() {
  const platforms = [
    {
      key: 'mt4',
      name: 'MetaTrader 4',
      file: 'TrackMyFxEA-v2.11.ex4',
      url: '/downloads/TrackMyFxEA-v2.11.ex4',
      logo: '/mt4.png',
      gradient: 'from-blue-500/10 to-cyan-500/5',
      borderHover: 'hover:border-blue-500/40',
      buttonGradient: 'from-blue-500 to-cyan-500',
      buttonGlow: 'shadow-blue-500/30',
      features: [
        'Real-time trade tracking (60s sync)',
        'All open + closed trades captured',
        'Floating drawdown monitoring',
        'Per-EA stats by magic number',
        'Account balance + equity tracking',
      ],
      setup: [
        'Download TrackMyFxEA-v2.11.ex4 below',
        'Open MetaTrader 4 → Press Ctrl+Shift+D (or File → Open Data Folder)',
        'Navigate to MQL4 → Experts folder',
        'Paste TrackMyFxEA-v2.11.ex4 file there',
        'Restart MT4 (or right-click Navigator → Refresh)',
        'Drag TrackMyFxEA from Navigator → Expert Advisors onto any chart',
        'In the EA settings, paste your API Key',
        'Enable "Allow Algo Trading" (top toolbar) — green smiley should appear on chart',
      ],
    },
    {
      key: 'mt5',
      name: 'MetaTrader 5',
      file: 'TrackMyFxEA-v2.11.ex5',
      url: '/downloads/TrackMyFxEA-v2.11.ex5',
      logo: '/mt5.png',
      gradient: 'from-emerald-500/10 to-teal-500/5',
      borderHover: 'hover:border-emerald-500/40',
      buttonGradient: 'from-emerald-500 to-teal-500',
      buttonGlow: 'shadow-emerald-500/30',
      features: [
        'Real-time trade tracking (60s sync)',
        'Hedge & netting account support',
        'Floating drawdown monitoring',
        'Per-EA stats by magic number',
        'Multi-currency cross-rate handling',
      ],
      setup: [
        'Download TrackMyFxEA-v2.11.ex5 below',
        'Open MetaTrader 5 → Press Ctrl+Shift+D (or File → Open Data Folder)',
        'Navigate to MQL5 → Experts folder',
        'Paste TrackMyFxEA-v2.11.ex5 file there',
        'Restart MT5 (or right-click Navigator → Refresh)',
        'Drag TrackMyFxEA from Navigator → Expert Advisors onto any chart',
        'In the EA settings, paste your API Key',
        'Enable "AlgoTrading" (top toolbar) — green play icon should appear on chart',
      ],
    },
  ];

  const triggerDownload = (platform) => {
    const link = document.createElement('a');
    link.href = platform.url;
    link.download = platform.file;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="max-w-6xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-100">Download EA</h1>
        <p className="text-sm text-gray-500 mt-1">
          Connect your MT4/MT5 to TrackMyFxEA in 60 seconds. Choose your platform below.
        </p>
      </div>

      {/* Reminder card */}
      <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 mb-6 flex items-start gap-3">
        <AlertCircle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="text-amber-400 font-semibold mb-1">Need an API Key?</p>
          <p className="text-gray-400">
            Generate one in{' '}
            <a href="/settings/api-keys" className="text-amber-400 underline hover:text-amber-300">
              Settings → API Keys
            </a>{' '}
            before attaching the EA. The key only shows once — copy it immediately.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {platforms.map((platform) => (
          <div
            key={platform.key}
            className={`bg-gradient-to-br ${platform.gradient} border border-dark-border ${platform.borderHover} rounded-2xl p-6 transition-all`}
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-14 h-14 rounded-xl bg-dark-surface border border-dark-border flex items-center justify-center overflow-hidden">
                <img src={platform.logo} alt={`${platform.name} logo`} className="w-10 h-10 object-contain" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-100">{platform.name}</h2>
                <p className="text-xs text-gray-500 font-mono">{platform.file}</p>
              </div>
            </div>

            {/* Download button */}
            <button
              onClick={() => triggerDownload(platform)}
              className={`w-full bg-gradient-to-r ${platform.buttonGradient} text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 hover:shadow-lg ${platform.buttonGlow} transition-all mb-6`}
            >
              <Download size={18} />
              Download {platform.name}
            </button>

            {/* Features */}
            <div className="mb-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Zap size={12} className="text-amber-400" /> Features
              </h3>
              <ul className="space-y-2">
                {platform.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                    <CheckCircle size={14} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Setup */}
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Setup Instructions
              </h3>
              <ol className="space-y-2.5">
                {platform.setup.map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-dark-surface border border-dark-border flex items-center justify-center text-[10px] font-bold text-gray-400">
                      {i + 1}
                    </span>
                    <span className="leading-relaxed">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        ))}
      </div>

      {/* Help section */}
      <div className="mt-8 bg-dark-card border border-dark-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-200 mb-2">Having trouble?</h3>
        <p className="text-sm text-gray-500 mb-3">
          The EA needs <span className="text-gray-300 font-mono text-xs">api.trackmyfxea.com</span> added to MT4/MT5's allowed URLs:
        </p>
        <ol className="text-sm text-gray-400 space-y-1.5 ml-4 list-decimal">
          <li>Tools → Options → <span className="text-gray-300 font-medium">Expert Advisors</span> tab</li>
          <li>Check "Allow WebRequest for listed URL"</li>
          <li>Add <code className="bg-dark-surface px-1.5 py-0.5 rounded text-emerald-400 font-mono text-xs">https://api.trackmyfxea.com</code> to the list</li>
          <li>Click OK and re-attach the EA</li>
        </ol>
        <a
          href="mailto:support@trackmyfxea.com"
          className="inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 mt-4"
        >
          <ExternalLink size={13} /> Contact support
        </a>
      </div>
    </div>
  );
}
